/**
 * Gallery Wall Editor component
 * Allows arranging multiple artworks in various gallery wall layouts
 */

import { useState } from 'react';
import {
  Slider,
  Card,
  CardContent,
  Switch,
  cn,
} from '@frameit/ui';
import { useProjectStore } from '../../stores/projectStore';
import type { GalleryLayoutType } from '@frameit/types';

/** Layout preset definition */
interface LayoutPreset {
  id: GalleryLayoutType;
  name: string;
  description: string;
  icon: string;
  minArtworks: number;
  maxArtworks: number;
}

/** Layout presets */
const LAYOUT_PRESETS: LayoutPreset[] = [
  {
    id: 'grid',
    name: 'Grid',
    description: 'Organized rows and columns',
    icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
    minArtworks: 2,
    maxArtworks: 12,
  },
  {
    id: 'horizontal',
    name: 'Horizontal',
    description: 'Single row arrangement',
    icon: 'M4 6h4v12H4V6zm6 0h4v12h-4V6zm6 0h4v12h-4V6z',
    minArtworks: 2,
    maxArtworks: 6,
  },
  {
    id: 'vertical',
    name: 'Vertical',
    description: 'Single column stack',
    icon: 'M6 4h12v4H6V4zm0 6h12v4H6v-4zm0 6h12v4H6v-4z',
    minArtworks: 2,
    maxArtworks: 5,
  },
  {
    id: 'salon',
    name: 'Salon',
    description: 'Organic, asymmetrical arrangement',
    icon: 'M4 4h5v6H4V4zm7 2h6v4h-6V6zm-7 8h4v6H4v-6zm6 2h5v4h-5v-4zm7-8h3v10h-3V6z',
    minArtworks: 3,
    maxArtworks: 10,
  },
  {
    id: 'symmetrical',
    name: 'Symmetrical',
    description: 'Mirrored around center',
    icon: 'M12 2v20M6 6h4v5H6V6zm8 0h4v5h-4V6zM6 13h4v5H6v-5zm8 0h4v5h-4v-5z',
    minArtworks: 2,
    maxArtworks: 8,
  },
  {
    id: 'staircase',
    name: 'Staircase',
    description: 'Diagonal stepping arrangement',
    icon: 'M4 4h4v4H4V4zm4 4h4v4H8V8zm4 4h4v4h-4v-4zm4 4h4v4h-4v-4z',
    minArtworks: 3,
    maxArtworks: 6,
  },
  {
    id: 'circular',
    name: 'Circular',
    description: 'Arranged in a circle',
    icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a1 1 0 110 2 1 1 0 010-2zm4 2a1 1 0 110 2 1 1 0 010-2zm2 4a1 1 0 110 2 1 1 0 010-2zm-2 4a1 1 0 110 2 1 1 0 010-2zm-4 2a1 1 0 110 2 1 1 0 010-2zm-4-2a1 1 0 110 2 1 1 0 010-2zm-2-4a1 1 0 110 2 1 1 0 010-2zm2-4a1 1 0 110 2 1 1 0 010-2z',
    minArtworks: 4,
    maxArtworks: 8,
  },
];

/** Background options for gallery wall */
const BACKGROUND_PRESETS = [
  { id: 'white', name: 'White', color: '#ffffff' },
  { id: 'cream', name: 'Cream', color: '#f5f5dc' },
  { id: 'light-grey', name: 'Light Grey', color: '#e0e0e0' },
  { id: 'grey', name: 'Grey', color: '#9e9e9e' },
  { id: 'charcoal', name: 'Charcoal', color: '#424242' },
  { id: 'navy', name: 'Navy', color: '#1a237e' },
  { id: 'forest', name: 'Forest', color: '#1b5e20' },
  { id: 'burgundy', name: 'Burgundy', color: '#800020' },
];

export interface GalleryWallEditorProps {
  className?: string;
}

/** Gallery wall editor component */
export function GalleryWallEditor({ className }: GalleryWallEditorProps) {
  const project = useProjectStore((s) => s.project);
  const setGalleryWall = useProjectStore((s) => s.setGalleryWall);
  const updateDisplayScale = useProjectStore((s) => s.updateDisplayScale);

  const [selectedLayout, setSelectedLayout] = useState<GalleryLayoutType>('grid');
  const [selectedBackground, setSelectedBackground] = useState('white');
  const [spacing, setSpacing] = useState(50);
  const [galleryScale, setGalleryScale] = useState(1);

  // Get all framed artworks from project
  const framedArtworks = project?.framedArtworks || [];
  const artworkCount = framedArtworks.length;

  // Check if gallery mode is enabled
  const galleryEnabled = !!project?.galleryWall;

  // Handle gallery toggle
  const handleToggle = (enabled: boolean) => {
    if (enabled && artworkCount >= 2) {
      // Enable gallery with default settings
      setGalleryWall({
        presetId: selectedLayout,
        sceneTemplateId: selectedBackground,
        zoneId: 'main',
        positions: framedArtworks.map((artwork, index) => ({
          framedArtworkId: artwork.id,
          position: index,
        })),
        spacing,
      });
    } else {
      // Disable gallery
      setGalleryWall(undefined);
    }
  };

  // Handle layout selection
  const handleLayoutSelect = (layoutId: GalleryLayoutType) => {
    setSelectedLayout(layoutId);

    if (galleryEnabled) {
      setGalleryWall({
        presetId: layoutId,
        sceneTemplateId: project?.galleryWall?.sceneTemplateId || selectedBackground,
        zoneId: 'main',
        positions: project?.galleryWall?.positions || framedArtworks.map((artwork, index) => ({
          framedArtworkId: artwork.id,
          position: index,
        })),
        spacing: project?.galleryWall?.spacing || spacing,
      });
    }
  };

  // Handle background selection
  const handleBackgroundSelect = (backgroundId: string) => {
    setSelectedBackground(backgroundId);

    if (galleryEnabled) {
      setGalleryWall({
        ...project!.galleryWall!,
        sceneTemplateId: backgroundId,
      });
    }
  };

  // Handle spacing change
  const handleSpacingChange = (value: number) => {
    setSpacing(value);

    if (galleryEnabled) {
      setGalleryWall({
        ...project!.galleryWall!,
        spacing: value,
      });
    }
  };

  // Get current layout preset
  const currentLayout = LAYOUT_PRESETS.find((l) => l.id === selectedLayout);

  // Check if we have enough artworks
  const hasEnoughArtworks = artworkCount >= 2;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Gallery toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Gallery Wall</h3>
            <p className="text-xs text-muted-foreground">
              Arrange multiple artworks together
            </p>
          </div>
          <Switch
            checked={galleryEnabled}
            onCheckedChange={handleToggle}
            disabled={!hasEnoughArtworks}
          />
        </div>
        {!hasEnoughArtworks && (
          <p className="text-xs text-amber-600 mt-2">
            Add at least 2 artworks to create a gallery wall
          </p>
        )}
      </div>

      {/* Layout selection */}
      <div className={cn('flex-1 overflow-auto p-4', !galleryEnabled && 'opacity-50 pointer-events-none')}>
        <h4 className="text-sm font-medium mb-3">Layout Style</h4>

        {/* Layout grid */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {LAYOUT_PRESETS.map((layout) => {
            const isDisabled = artworkCount < layout.minArtworks || artworkCount > layout.maxArtworks;

            return (
              <button
                key={layout.id}
                onClick={() => !isDisabled && handleLayoutSelect(layout.id)}
                disabled={isDisabled}
                className={cn(
                  'relative p-3 rounded-lg border-2 transition-all text-left',
                  'hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary',
                  selectedLayout === layout.id
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/30',
                  isDisabled && 'opacity-40 cursor-not-allowed hover:scale-100'
                )}
              >
                <div className="flex items-start gap-2">
                  <svg
                    className="w-6 h-6 text-muted-foreground flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d={layout.icon} />
                  </svg>
                  <div>
                    <span className="text-sm font-medium block">{layout.name}</span>
                    <span className="text-xs text-muted-foreground">{layout.description}</span>
                    <span className="text-xs text-muted-foreground block mt-1">
                      {layout.minArtworks}-{layout.maxArtworks} artworks
                    </span>
                  </div>
                </div>
                {selectedLayout === layout.id && (
                  <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Current layout info */}
        {currentLayout && (
          <div className="text-xs text-center text-muted-foreground mb-4 p-2 bg-muted/50 rounded">
            <strong>{currentLayout.name}:</strong> {currentLayout.description}
            <br />
            You have {artworkCount} artwork{artworkCount !== 1 ? 's' : ''} selected
          </div>
        )}

        {/* Background color */}
        <h4 className="text-sm font-medium mb-3">Wall Color</h4>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {BACKGROUND_PRESETS.map((bg) => (
            <button
              key={bg.id}
              onClick={() => handleBackgroundSelect(bg.id)}
              className={cn(
                'w-full aspect-square rounded-lg border-2 transition-all',
                'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary',
                selectedBackground === bg.id
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-transparent'
              )}
              style={{ backgroundColor: bg.color }}
              title={bg.name}
            >
              {selectedBackground === bg.id && (
                <div className="w-full h-full flex items-center justify-center">
                  <svg
                    className={cn(
                      'w-4 h-4',
                      bg.id === 'white' || bg.id === 'cream' || bg.id === 'light-grey'
                        ? 'text-gray-800'
                        : 'text-white'
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Spacing controls */}
      <Card className={cn('m-4 mt-0', !galleryEnabled && 'opacity-50 pointer-events-none')}>
        <CardContent className="p-4 space-y-4">
          <h4 className="text-sm font-medium">Layout Settings</h4>

          {/* Overall gallery scale */}
          <div>
            <label className="text-xs text-muted-foreground">Gallery Scale</label>
            <Slider
              value={galleryScale}
              min={0.5}
              max={1.5}
              step={0.05}
              onChange={(value) => {
                setGalleryScale(value);
                // Update all artwork scales proportionally
                framedArtworks.forEach((artwork) => {
                  updateDisplayScale(artwork.id, value);
                });
              }}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Spacing */}
          <div>
            <label className="text-xs text-muted-foreground">Frame Spacing</label>
            <Slider
              value={spacing}
              min={20}
              max={150}
              step={5}
              onChange={handleSpacingChange}
              showValue
              formatValue={(v) => `${v}mm`}
            />
          </div>

          {/* Artwork count info */}
          <div className="text-xs text-muted-foreground pt-2 border-t">
            <div className="flex justify-between">
              <span>Artworks in gallery:</span>
              <span className="font-medium">{artworkCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
